import React, { useState } from 'react';
import { 
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText, 
  Typography, Divider, Paper, Collapse, Toolbar, Stack
} from '@mui/material';
import { 
  ExpandLess, ExpandMore, Storage, Archive, 
  Security, DataUsage, 
  Person,
  CloudUpload, SolarPower, GetApp, Help, Dashboard, Assessment, Settings
} from '@mui/icons-material';
import { useViewContext } from '../context/ViewContext';
import { useAuth } from '../auth/AuthContext';
import sabalLogo from '../assets/sabal-logo.png';

/**
 * Sidebar navigation component with collapsible sections and view switching.
 * On mobile (isMobile=true), renders as a temporary slide-over drawer.
 * On desktop, renders as a permanent sidebar (unchanged from original).
 */
const SidebarNavigation = ({ isMobile = false, mobileOpen = false, onMobileClose }) => {
  const { currentView, setView } = useViewContext();
  const auth = useAuth();
  
  // Collapsed section states
  const [fileOpen, setFileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(true);
  
  // Handle view switching — auto-close drawer on mobile
  const handleViewChange = (view) => {
    setView(view);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };
  
  // Drawer width
  const drawerWidth = 240;

  const drawerContent = (
    <>
      {/* Drawer header */}
      <Toolbar sx={{ 
        bgcolor: 'white', 
        color: 'text.primary',
        borderBottom: '1px solid #e0e0e0',
        py: 2
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
          <Box 
            component="img" 
            src={sabalLogo} 
            alt="Sabal Logo" 
            sx={{ 
              width: 32, 
              height: 32,
              objectFit: 'contain'
            }} 
          />
          <Stack>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                lineHeight: 1.1,
                color: 'text.primary'
              }}
            >
              Sabal SOMEAC
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 500, 
                lineHeight: 1.1,
                color: 'text.secondary'
              }}
            >
              Administrative Console
            </Typography>
          </Stack>
        </Stack>
      </Toolbar>
      
      <List sx={{ px: 1, overflowY: 'auto' }}>
        <ListItem
          button
          onClick={() => handleViewChange('dashboard')}
          selected={currentView === 'dashboard'}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><Dashboard /></ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>

        {/* View options section */}
        <ListItem 
          button 
          onClick={() => setViewOpen(!viewOpen)}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><DataUsage /></ListItemIcon>
          <ListItemText primary="Live Data Views" />
          {viewOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={viewOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem 
              button 
              sx={{ pl: 4, borderRadius: 1, ml: 1, mb: 0.5 }} 
              onClick={() => handleViewChange('canopyView')}
              selected={currentView === 'canopyView'}
            >
              <ListItemIcon><SolarPower /></ListItemIcon>
              <ListItemText primary="Canopy Telemetry" />
            </ListItem>
            <ListItem 
              button 
              sx={{ pl: 4, borderRadius: 1, ml: 1, mb: 0.5 }} 
              onClick={() => handleViewChange('fleetView')}
              selected={currentView === 'fleetView'}
            >
              <ListItemIcon><DataUsage /></ListItemIcon>
              <ListItemText primary="Fleet Telemetry" />
            </ListItem>
          </List>
        </Collapse>
        
        {/* Browser command - promoted to same level as Data Operations */}
        <ListItem 
          button 
          onClick={() => handleViewChange('browserView')}
          selected={currentView === 'browserView'}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><Storage /></ListItemIcon>
          <ListItemText primary="Telemetry Browser" />
        </ListItem>

        <ListItem
          button
          onClick={() => handleViewChange('energyReports')}
          selected={currentView === 'energyReports'}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><Assessment /></ListItemIcon>
          <ListItemText primary="Energy Reports" />
        </ListItem>

        <ListItem button onClick={() => handleViewChange('settings')} selected={currentView === 'settings'} sx={{ borderRadius: 1, mb: 0.5 }}>
          <ListItemIcon><Settings /></ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>
        
        <Divider sx={{ my: 1 }} />
        
        {/* File Operations section */}
        <ListItem 
          button 
          onClick={() => setFileOpen(!fileOpen)}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><Archive /></ListItemIcon>
          <ListItemText primary="File Operations" />
          {fileOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        
        <Collapse in={fileOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem 
              button 
              sx={{ pl: 4, borderRadius: 1, ml: 1, mb: 0.5 }} 
              onClick={() => handleViewChange('archiveView')}
              selected={currentView === 'archiveView'}
            >
              <ListItemIcon><Archive /></ListItemIcon>
              <ListItemText primary="Telemetry Archive" />
            </ListItem>
            <ListItem 
              button 
              sx={{ pl: 4, borderRadius: 1, ml: 1, mb: 0.5 }} 
              onClick={() => handleViewChange('importData')}
              selected={currentView === 'importData'}
            >
              <ListItemIcon><CloudUpload /></ListItemIcon>
              <ListItemText primary="Load Telemetry Archive" />
            </ListItem>
          </List>
        </Collapse>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Admin section (superuser only) */}
        {auth.isSuperuser && (
          <>
            <ListItem 
              button 
              onClick={() => setAdminOpen(!adminOpen)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon><Security /></ListItemIcon>
              <ListItemText primary="Administration" />
              {adminOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem 
                  button 
                  sx={{ pl: 4, borderRadius: 1, ml: 1, mb: 0.5 }} 
                  onClick={() => handleViewChange('userManagement')}
                  selected={currentView === 'userManagement'}
                >
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText primary="User Management" />
                </ListItem>
              </List>
            </Collapse>
          </>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Downloads & Help — visible to all users */}
        <ListItem 
          button 
          onClick={() => handleViewChange('downloads')}
          selected={currentView === 'downloads'}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><GetApp /></ListItemIcon>
          <ListItemText primary="Downloads" />
        </ListItem>
        <ListItem 
          button 
          onClick={() => handleViewChange('help')}
          selected={currentView === 'help'}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon><Help /></ListItemIcon>
          <ListItemText primary="Help & Instructions" />
        </ListItem>
      </List>
    </>
  );

  // Mobile: temporary drawer (slide-over, closes on backdrop click or view selection)
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent sidebar (unchanged from original)
  return (
    <Box sx={{ 
      width: drawerWidth,
      flexShrink: 0,
      height: '100%',
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          width: drawerWidth,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              position: 'static',
              width: drawerWidth,
              border: 'none',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Paper>
    </Box>
  );
};

export default SidebarNavigation;
